'use client'

import { Divider } from '@macarongfactory/web-ui'
import { useEffect, useMemo, useRef, useState } from 'react'

import { getApiPath } from '@/app/(makers)/_utils'
import { useSectionScrollSpy } from '@/shared/model'

import AppPromoBanner from './AppPromoBanner'
import BottomCta from './BottomCta'
import CarCompareSection from './CarCompareSection'
import CompareRankSection from './CompareRankSection'
import DataDisclaimer from './DataDisclaimer'
import FuelCostSection from './FuelCostSection'
import HeroCard from './HeroCard'
import InAppTopBar, { IN_APP_TOP_BAR_HEIGHT_PX } from './InAppTopBar'
import MaintenanceSection from './MaintenanceSection'
import OwnerRatingDialog from './OwnerRatingDialog'
import OwnerReviewSection from './OwnerReviewSection'
import RatingCompleteDialog from './RatingCompleteDialog'
import RealFuelSection from './RealFuelSection'
import ReviewPromptDialog from './ReviewPromptDialog'
import SearchHeader from './SearchHeader'
import SectionTabs, { type ISectionTabItem } from './SectionTabs'
import ShareSection from './ShareSection'
import SideCta from './SideCta'
import {
  LOCK_AB_EXPOSURE_LABEL,
  LOCK_AB_VARIANT,
} from '../../_features/modoocar-lock-ab'
import {
  LockAbProvider,
  useLockAbExposure,
} from '../../_features/modoocar-lock-ab/_hooks/useLockAb'
import {
  FUEL_DOMAIN_TO_API,
  MODOOCAR_CODE,
  MODOOCAR_EVENT,
  ModoocarTrackingProvider,
  mapSummaryToDetail,
  useModoocarTracking,
  useOwnerReviewDialogs,
} from '../_features/modoocar'

import type { ILockAbAssignment } from '../../_features/modoocar-lock-ab'
import type {
  IDashboardSummaryResponse,
  IModoocarDetail,
  TFuelType,
} from '../_features/modoocar'
import type { CSSProperties } from 'react'

interface IModoocarDetailProps {
  detail: IModoocarDetail
  segment?: string
  /** [앱]: 내 차 식별자는 인앱 웹뷰 요청 헤더(x-mycle-macar-id)로 받습니다. */
  macarId?: string | null
  /** [주의]: 인앱 여부는 서버에서 판정해서 넘겨야 합니다. 클라이언트에서 판정하면 CTA와 헤더가 마운트된 뒤에 사라지며 깜빡입니다. */
  isInApp: boolean
  lockAb: ILockAbAssignment
}

const PageShowTracker = () => {
  const { pageShow } = useModoocarTracking()
  const firedRef = useRef(false)
  useEffect(() => {
    if (firedRef.current) return
    firedRef.current = true
    pageShow()
  }, [pageShow])
  return null
}

const LockAbExposureTracker = ({
  assignment,
}: {
  assignment: ILockAbAssignment
}) => {
  const { track } = useModoocarTracking()
  useLockAbExposure(assignment, ({ variant, from }) =>
    track(MODOOCAR_EVENT.SECTION_SHOW, {
      code: MODOOCAR_CODE.AB_EXPOSURE,
      label: LOCK_AB_EXPOSURE_LABEL,
      status: variant,
      from,
    })
  )
  return null
}

const TAB_ITEMS: ISectionTabItem[] = [
  { id: 'real-fuel', label: '실연비' },
  { id: 'fuel-cost', label: '유류비' },
  { id: 'owner-review', label: '오너 리뷰' },
  { id: 'compare', label: '차종 비교' },
  { id: 'maintenance', label: '정비 리뷰' },
]

const ThickDivider = () => <Divider.Horizontal strokeWidth='stroke-width-x10' />

const ModoocarDetail = ({
  detail,
  segment,
  macarId,
  isInApp,
  lockAb,
}: IModoocarDetailProps) => {
  const isLock = lockAb.variant === LOCK_AB_VARIANT.LOCK
  const [fuelType, setFuelType] = useState<TFuelType>(detail.fuelType)
  const [localDetail, setLocalDetail] = useState<IModoocarDetail | null>(null)
  const activeDetail = localDetail ?? detail
  const hasCompare =
    !activeDetail.isMotorcycle &&
    Boolean(activeDetail.segment) &&
    activeDetail.compare.some(item => !item.current)
  // [백엔드]: 별점만 남긴 평가는 집계 개수(ownerReviewCount)에는 잡히지만 후기 목록에는 나오지 않습니다.
  // 그래서 집계 개수가 아니라 실제 후기 목록으로 노출 여부를 판단합니다. (WEB-6294)
  const hasOwnerReview = activeDetail.ownerReview.reviews.length > 0
  const hasRankChips =
    !activeDetail.isMotorcycle &&
    Boolean(activeDetail.segment) &&
    (activeDetail.satisfactionRank > 0 ||
      activeDetail.efficiencyRank > 0 ||
      activeDetail.fuelCostPercentile > 0)
  // [정책]: 잠금 화면(B)에서는 '다른 차와 비교하면?'이 차종 비교 섹션이라서, 비교 표가 없어도 랭킹 칩이 있으면 섹션과 탭을 보여 줍니다.
  const showCompareSection = isLock ? hasRankChips || hasCompare : hasCompare
  const isElectric = activeDetail.fuelType === '전기'
  const tabItems = useMemo(() => {
    const base = TAB_ITEMS.filter(t => {
      if (t.id === 'compare') return showCompareSection
      if (t.id === 'owner-review') return hasOwnerReview
      return true
    })
    if (!isElectric) return base
    return base.map(item => {
      if (item.id === 'real-fuel') return { ...item, label: '실전비' }
      if (item.id === 'fuel-cost') return { ...item, label: '전기 요금' }
      return item
    })
  }, [showCompareSection, hasOwnerReview, isElectric])

  // [앱]: 안드로이드 앱은 replaceState로 주소를 바꿔도 새 웹뷰를 열어서 /v2/v2/... 경로로 404가 납니다.
  // 그래서 인앱에서는 주소를 바꾸지 않고 BFF로 summary를 다시 조회합니다.
  const handleInAppFuelRefresh = async (fuel: TFuelType) => {
    const current = localDetail ?? detail
    const params = new URLSearchParams({
      brandName: current.brand,
      // [주의]: 세부모델명으로 조회해야 합니다. 상위모델명으로 조회하면 SM6(LFD)가 SM6로 바뀌는 것처럼 세부모델이 상위모델로 되돌아갑니다.
      modelName: current.lowerModelName,
      fuelType: FUEL_DOMAIN_TO_API[fuel],
      vehicleType: current.isMotorcycle ? '모터사이클' : '자동차',
    })
    // [정책]: 유종을 바꿔도 같은 랭킹 기준으로 보이도록 진입 세그먼트를 유지합니다.
    if (segment) params.set('segment', segment)
    try {
      const res = await fetch(getApiPath(`/api/modoocar/summary?${params}`))
      if (!res.ok) return
      const summary: IDashboardSummaryResponse = await res.json()
      const mapped = mapSummaryToDetail(summary, current)
      setLocalDetail({
        ...mapped,
        ownerReviewCount: current.ownerReviewCount,
        carOwnerReviewCount: current.carOwnerReviewCount,
      })
    } catch {
      // fetch 실패 시 기존 데이터 유지
    }
  }

  const {
    activeDialog,
    score,
    setScore,
    isSubmitting,
    closeDialog,
    confirmRating,
    openReviewPromptIfNeeded,
  } = useOwnerReviewDialogs({ enabled: isInApp, macarId, vehicle: detail })

  // [주의]: 탭을 눌러 스크롤하는 동안에는 목표 탭에 닿을 때까지 scroll-spy를 잠급니다. 잠그지 않으면 중간 탭이 차례로 깜빡입니다.
  const sectionIds = useMemo(() => tabItems.map(({ id }) => id), [tabItems])
  // [주의]: sticky 헤더 높이에 맞춘 값입니다(웹: 검색 48 + 탭 49 = 97, 인앱: 앱바 56 + 탭 49 = 105). 헤더 높이가 바뀌면 탭 이동 위치가 어긋납니다.
  const { activeId: activeTab, scrollToSection: handleScrollTo } =
    useSectionScrollSpy({ sectionIds, triggerOffset: isInApp ? 128 : 120 })

  return (
    <LockAbProvider assignment={lockAb}>
      <ModoocarTrackingProvider
        company={activeDetail.brand}
        modelName={activeDetail.lowerModelName}
        fuelType={fuelType}
      >
        <PageShowTracker />
        <LockAbExposureTracker assignment={lockAb} />
        <div className='lg:min-h-screen lg:bg-background-surface-basement'>
          {isInApp ? (
            <InAppTopBar title={activeDetail.lowerModelName} />
          ) : (
            <SearchHeader />
          )}

          <div className='mx-auto w-full max-w-[1024px]'>
            <div className='lg:flex lg:gap-dimension-x6 lg:px-dimension-x5 lg:pt-dimension-x4'>
              <main
                className='flex w-full flex-col bg-background-surface-default lg:w-[640px] lg:shrink-0 lg:rounded-t-dimension-x5 lg:shadow-[0px_2px_2px_0px_rgba(0,0,0,0.06),0px_2px_6px_0px_rgba(0,0,0,0.08)]'
                style={
                  isInApp
                    ? ({
                        '--mc-tab-top': `${IN_APP_TOP_BAR_HEIGHT_PX}px`,
                        '--mc-section-scroll-mt': `${IN_APP_TOP_BAR_HEIGHT_PX + 49}px`,
                      } as CSSProperties)
                    : undefined
                }
              >
                <HeroCard
                  detail={activeDetail}
                  fuelType={fuelType}
                  onFuelChange={setFuelType}
                  onScrollTo={handleScrollTo}
                  isInApp={isInApp}
                  onInAppFuelRefresh={
                    isInApp ? handleInAppFuelRefresh : undefined
                  }
                />
                {!isInApp && !isLock && <AppPromoBanner />}
                <SectionTabs
                  items={tabItems}
                  activeId={activeTab}
                  onSelect={handleScrollTo}
                />

                <RealFuelSection detail={activeDetail} />
                <ThickDivider />
                <FuelCostSection detail={activeDetail} />
                <ThickDivider />
                {hasOwnerReview ? (
                  <>
                    <OwnerReviewSection
                      detail={activeDetail}
                      isInApp={isInApp}
                      onMoreClick={openReviewPromptIfNeeded}
                    />
                    <ThickDivider />
                  </>
                ) : null}
                {isLock ? (
                  showCompareSection ? (
                    <>
                      <div
                        id='compare'
                        className='flex scroll-mt-[var(--mc-section-scroll-mt,97px)] flex-col lg:scroll-mt-[112px]'
                      >
                        {hasRankChips ? (
                          <CompareRankSection
                            detail={activeDetail}
                            fuelType={fuelType}
                          />
                        ) : null}
                        {hasCompare ? (
                          <CarCompareSection
                            detail={activeDetail}
                            asSubsection
                          />
                        ) : null}
                      </div>
                      <ThickDivider />
                    </>
                  ) : null
                ) : hasCompare ? (
                  <>
                    <CarCompareSection detail={activeDetail} />
                    <ThickDivider />
                  </>
                ) : null}
                <MaintenanceSection detail={activeDetail} isInApp={isInApp} />
                <ThickDivider />
                <ShareSection
                  title={`${activeDetail.brand} ${activeDetail.upperModelName}`}
                  imageUrl={activeDetail.carImageUrl}
                  isInApp={isInApp}
                />

                <DataDisclaimer detail={activeDetail} />
              </main>

              {!isInApp && (
                <aside className='hidden lg:block lg:w-[320px] lg:shrink-0'>
                  <SideCta managingUserCount={activeDetail.managingUserCount} />
                </aside>
              )}
            </div>

            {/* [앱]: 인앱 웹뷰에는 앱의 네이티브 하단바가 있어서 하단 고정 CTA를 숨깁니다. */}
            {!isInApp && (
              <BottomCta managingUserCount={activeDetail.managingUserCount} />
            )}
          </div>
        </div>

        {isInApp ? (
          <>
            <OwnerRatingDialog
              open={activeDialog === 'rating'}
              modelName={activeDetail.lowerModelName}
              score={score}
              onScoreChange={setScore}
              onClose={closeDialog}
              onConfirm={confirmRating}
              isSubmitting={isSubmitting}
            />
            <RatingCompleteDialog
              open={activeDialog === 'complete'}
              score={score}
              onClose={closeDialog}
              onViewReviews={() => {
                closeDialog()
                handleScrollTo('owner-review')
              }}
            />
            <ReviewPromptDialog
              open={activeDialog === 'reviewPrompt'}
              onClose={closeDialog}
            />
          </>
        ) : null}
      </ModoocarTrackingProvider>
    </LockAbProvider>
  )
}

export default ModoocarDetail
